import React from 'react'

import { Controller } from 'react-hook-form'

import { DropdownSelect, TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, object, objectOf, string } from 'prop-types'

const STREET_ADDRESS_MAX_LENGTH = 200

function CompanyForm({
  control,
  errors,
  countries,
  countriesWithStates,
  states,
  translate,
  watchCountry = '',
  isDisabled = false,
  onFieldChange,
  setValue,
}) {
  const showStateField = countriesWithStates.includes(watchCountry)
  const stateOptions = watchCountry ? (states[watchCountry] || []) : []

  const requiredMessage = translate('subscription_checkout.company.field_required')
  const countrySelectOptions = countries.map(([name, code]) => ({ value: code, label: name }))
  const stateSelectOptions = stateOptions.map(([name, code]) => ({ value: code, label: name }))

  return (
    <div className="company-form">
      <Controller
        name="companyName"
        control={control}
        rules={{ required: requiredMessage }}
        render={({ field }) => (
          <TextInput
            id="company-name"
            isPilot
            type="primary"
            size="medium"
            label={translate('subscription_checkout.company.company_name')}
            isRequired
            isWarning={!!errors.companyName}
            helpText={errors.companyName?.message || ''}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value)
              onFieldChange()
            }}
            wrapperClassName="company-form__field"
            isNotSpaced
            disabled={isDisabled}
          />
        )}
      />

      <div className="company-form__row">
        <Controller
          name="companyCode"
          control={control}
          render={({ field }) => (
            <TextInput
              id="company-code"
              isPilot
              type="primary"
              size="medium"
              label={translate('subscription_checkout.company.company_code')}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value)
                onFieldChange()
              }}
              wrapperClassName="company-form__field company-form__field--half"
              isNotSpaced
              disabled={isDisabled}
            />
          )}
        />

        <Controller
          name="vatin"
          control={control}
          render={({ field }) => (
            <TextInput
              id="company-vatin"
              isPilot
              type="primary"
              size="medium"
              label={translate('subscription_checkout.company.vatin')}
              tooltip={translate('subscription_checkout.company.vatin_tooltip')}
              isWarning={!!errors.vatin}
              helpText={errors.vatin?.message || ''}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value)
                onFieldChange()
              }}
              wrapperClassName="company-form__field company-form__field--half"
              isNotSpaced
              disabled={isDisabled}
            />
          )}
        />
      </div>

      <Controller
        name="country"
        control={control}
        rules={{ required: requiredMessage }}
        render={({ field }) => (
          <div className="company-form__field">
            <label className="cgt-legacy-text-caption company-form__label" htmlFor="company-country">
              {translate('subscription_checkout.company.country')}
              <b className="cgt-legacy-color-utility-danger-100"> *</b>
            </label>
            <DropdownSelect
              id="company-country"
              initialOptions={countrySelectOptions}
              initialValue={countrySelectOptions.find((o) => o.value === field.value)}
              onChange={(option) => {
                field.onChange(option?.value ?? '')
                setValue('state', '')
                onFieldChange()
              }}
              isDisabled={isDisabled}
              isWarning={!!errors.country}
              placeholder="Select"
            />
            {errors.country && (
              <span className="cgt-legacy-text-tiny cgt-legacy-color-utility-danger-100 company-form__error">
                {errors.country.message}
              </span>
            )}
          </div>
        )}
      />

      {showStateField && (
        <Controller
          name="state"
          control={control}
          rules={{ required: requiredMessage }}
          render={({ field }) => (
            <div className="company-form__field">
              <label className="cgt-legacy-text-tiny cgt-legacy-color-grey-60 company-form__label" htmlFor="company-state">
                {translate('subscription_checkout.company.state')}
                <b className="cgt-legacy-color-utility-danger-100"> *</b>
              </label>
              <DropdownSelect
                id="company-state"
                initialOptions={stateSelectOptions}
                initialValue={stateSelectOptions.find((o) => o.value === field.value)}
                onChange={(option) => {
                  field.onChange(option?.value ?? '')
                  onFieldChange()
                }}
                isDisabled={isDisabled}
                isWarning={!!errors.state}
                placeholder="Select"
              />
              {errors.state && (
                <span className="cgt-legacy-text-tiny cgt-legacy-color-utility-danger-100 company-form__error">
                  {errors.state.message}
                </span>
              )}
            </div>
          )}
        />
      )}

      <div className="company-form__row">
        <Controller
          name="zipCode"
          control={control}
          rules={{ required: requiredMessage }}
          render={({ field }) => (
            <TextInput
              id="company-zip"
              isPilot
              type="primary"
              size="medium"
              label={translate('subscription_checkout.company.zip_code')}
              isRequired
              isWarning={!!errors.zipCode}
              helpText={errors.zipCode?.message || ''}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value)
                onFieldChange()
              }}
              wrapperClassName="company-form__field company-form__field--half"
              isNotSpaced
              disabled={isDisabled}
            />
          )}
        />

        <Controller
          name="city"
          control={control}
          rules={{ required: requiredMessage }}
          render={({ field }) => (
            <TextInput
              id="company-city"
              isPilot
              type="primary"
              size="medium"
              label={translate('subscription_checkout.company.city')}
              isRequired
              isWarning={!!errors.city}
              helpText={errors.city?.message || ''}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value)
                onFieldChange()
              }}
              wrapperClassName="company-form__field company-form__field--half"
              isNotSpaced
              disabled={isDisabled}
            />
          )}
        />
      </div>

      <Controller
        name="streetAddress"
        control={control}
        rules={{ required: requiredMessage }}
        render={({ field }) => (
          <TextInput
            id="company-street"
            isPilot
            type="primary"
            size="medium"
            label={translate('subscription_checkout.company.street_address')}
            isRequired
            isWarning={!!errors.streetAddress}
            helpText={errors.streetAddress?.message || ''}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value)
              onFieldChange()
            }}
            maxLength={STREET_ADDRESS_MAX_LENGTH}
            wrapperClassName="company-form__field"
            isNotSpaced
            disabled={isDisabled}
          />
        )}
      />
    </div>
  )
}

CompanyForm.propTypes = {
  control: object.isRequired,
  errors: object.isRequired,
  countries: arrayOf(arrayOf(string)).isRequired,
  countriesWithStates: arrayOf(string).isRequired,
  states: objectOf(arrayOf(arrayOf(string))).isRequired,
  translate: func.isRequired,
  watchCountry: string,
  isDisabled: bool,
  onFieldChange: func.isRequired,
  setValue: func.isRequired,
}

export default CompanyForm
